import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import loadPaymentProfiles from "@insite/client-framework/Store/Data/PaymentProfiles/Handlers/LoadPaymentProfiles";
import { getPaymentProfilesDataView } from "@insite/client-framework/Store/Data/PaymentProfiles/PaymentProfilesSelectors";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CENAccountDetailsAddressInfo from "@tCentro/Components/CENAccountDetailsAddressInfo";
import CENArrowRightLong from "@tCentro/Icons/CENArrowRightLong";
import { COLORS } from "@tCentro/theme/design";
import React, { useContext, useEffect } from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    backgroundColor = "backgroundColor",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    currentBillTo: getCurrentBillToState(state),
    currentShipTo: getCurrentShipToState(state),
    accountSettingsPageUrl: getPageLinkByPageType(state, "AccountSettingsPage")?.url,
    savedPaymentPageUrl: getPageLinkByPageType(state, "SavedPaymentsPage")?.url,
    paymentProfilesDataView: getPaymentProfilesDataView(state, { pageSize: 999 }),
});

const mapDispatchToProps = {
    loadCurrentBillTo,
    loadCurrentShipTo,
    loadPaymentProfiles,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface CENAccountDetailsStyles {
    gridContainer?: GridContainerProps;
    innerContainer?: GridContainerProps;
    headingGridItem?: GridItemProps;
    iconOne?: IconPresentationProps;
    headingText?: TypographyProps;
    billToAddressInfoDisplayGridItem?: GridItemProps;
    shipToAddressInfoDisplayGridItem?: GridItemProps;
    paymentGridItem?: GridItemProps;
    addressHeading?: GridItemProps;
    addressInfo?: GridItemProps;
    editAddress?: GridItemProps;
    editLink?: InjectableCss;
    cardText?: InjectableCss;
}

export const cenAccountDetailsStyles: CENAccountDetailsStyles = {
    gridContainer: {
        gap: 32,
        css: css`
            border: 4px solid ${COLORS.background.grayShade};
            padding: 28px 20px 28px 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 16px 10px 16px 10px;
                    `,
                    css`
                        padding: 16px 10px 16px 10px;
                    `,
                    css`
                        padding: 16px 10px 16px 10px;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    innerContainer: {
        css: css`
            padding: 16px 16px 16px 16px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            justify-content: space-between;
            align-items: center;
            h2 {
                font-weight: 600;
                font-size: 18px;
                line-height: 24px;
                color: ${getProp("theme.typography.h2.color")};
            }
            span {
                span {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    font-size: 14px;
                    line-height: 16px;
                    font-weight: 600;
                }
            }
            a {
                span {
                    display: flex;
                    align-items: center;
                    font-weight: 600;
                    font-size: 14px;
                    line-height: 16px;
                }
            }
        `,
    },
    iconOne: {
        size: 16,
        css: css`
            svg {
                > path {
                    fill: ${getColor("text.link")};
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 12px;
                        height: 12px;
                    `,
                    css`
                        width: 12px;
                        height: 12px;
                    `,
                    css`
                        width: 14px;
                        height: 14px;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    headingText: {
        variant: "h2",
        css: css`
            font-weight: 600 !important;
            font-size: 16px !important;
            line-height: 24px !important;
            margin-bottom: 16px;
        `,
    },
    billToAddressInfoDisplayGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    shipToAddressInfoDisplayGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    paymentGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    addressHeading: { width: 12 },
    addressInfo: { width: 12 },
    editAddress: { width: 12 },
    editLink: {
        css: css`
            font-weight: 600;
            font-size: 14px;
            line-height: 16px;
        `,
    },
    cardText: {
        css: css`
            color: ${getProp("theme.typography.p.color")};
            font-weight: 400;
            font-size: 16px;
            line-height: 24px;
            margin-bottom: 12px;
        `,
    },
};

const styles = cenAccountDetailsStyles;

const CENAccountDetails: FC<Props> = (props: Props) => {
    const {
        currentBillTo,
        currentShipTo,
        loadCurrentBillTo,
        loadCurrentShipTo,
        paymentProfilesDataView,
        loadPaymentProfiles,
        fields,
    } = props;

    useEffect(() => {
        if (!paymentProfilesDataView.value && !paymentProfilesDataView.isLoading) {
            loadPaymentProfiles({ pageSize: 999 });
        }
    }, [paymentProfilesDataView, loadPaymentProfiles]);

    const defaultCard = paymentProfilesDataView?.value?.find(o => o.isDefault);

    useEffect(() => {
        if (!currentBillTo.value && !currentBillTo.isLoading) {
            loadCurrentBillTo();
        }
    }, [currentBillTo]);

    useEffect(() => {
        if (!currentShipTo.value && !currentShipTo.isLoading) {
            loadCurrentShipTo();
        }
    }, [currentShipTo]);
    if (!currentShipTo.value || !currentBillTo.value) {
        return null;
    }
    return (
        <>
            <StyledWrapper
                css={css`
                    height: 16px;
                    background: ${fields.backgroundColor};
                `}
            ></StyledWrapper>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.headingGridItem}>
                    <Typography as="h2" variant="h2">
                        {translate("ACCOUNT DETAILS")}
                    </Typography>
                    <Link href={props.accountSettingsPageUrl}>
                        Account Settings <Icon src={CENArrowRightLong} {...styles.iconOne}></Icon>
                    </Link>
                </GridItem>
                <GridContainer {...styles.innerContainer}>
                    <GridItem {...styles.billToAddressInfoDisplayGridItem}>
                        <Typography as="h2" {...styles.headingText}>
                            {translate("Bill To")}
                        </Typography>

                        <CENAccountDetailsAddressInfo
                            companyName={currentBillTo.value.companyName}
                            address1={currentBillTo.value.address1}
                            city={currentBillTo.value.city}
                            state={currentBillTo.value.state ? currentBillTo.value.state.abbreviation : undefined}
                            postalCode={currentBillTo.value.postalCode}
                            phone={currentBillTo.value.phone}
                        />
                        <Link
                            href={props.accountSettingsPageUrl}
                            data-test-selector="billTo_Edit_Address"
                            {...styles.editLink}
                        >
                            {translate("Edit Default")}
                        </Link>
                    </GridItem>
                    <GridItem {...styles.shipToAddressInfoDisplayGridItem}>
                        <Typography as="h2" {...styles.headingText}>
                            {translate("Ship To")}
                        </Typography>

                        <CENAccountDetailsAddressInfo
                            companyName={currentShipTo.value.companyName}
                            address1={currentShipTo.value.address1}
                            city={currentShipTo.value.city}
                            state={currentShipTo.value.state ? currentShipTo.value.state.abbreviation : undefined}
                            postalCode={currentShipTo.value.postalCode}
                            phone={currentShipTo.value.phone}
                        />
                        <Link
                            href={props.accountSettingsPageUrl}
                            data-test-selector="shipTo_Edit_Address"
                            {...styles.editLink}
                        >
                            {translate("Edit Default")}
                        </Link>
                    </GridItem>
                    <GridItem {...styles.paymentGridItem}>
                        <Typography as="h2" {...styles.headingText}>
                            {translate("Saved Payments")}
                        </Typography>
                        {defaultCard ? (
                            <Typography {...styles.cardText}>
                                {`${defaultCard.cardType} ${translate(
                                    "ending in",
                                )} ${defaultCard.maskedCardNumber.substring(defaultCard.maskedCardNumber.length - 4)}`}
                            </Typography>
                        ) : (
                            <Typography {...styles.cardText}>{translate("No Default Card")}</Typography>
                        )}
                        <Link href={props.savedPaymentPageUrl} data-test-selector="editButton" {...styles.editLink}>
                            {translate("Edit Default")}
                        </Link>
                    </GridItem>
                </GridContainer>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CENAccountDetails),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.backgroundColor,
                editorTemplate: "TextField",
                defaultValue: "linear-gradient(90deg,#266b40 27.08%,#2d804c 71.87%,#43687a 100%)",
                fieldType: "General",
                displayName: "Background Color",
            },
        ],
    },
};

export default widgetModule;
